import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import Loader from "@/components/ui/Loader";
import { Dot } from "components/Dot";
import { postFetcher, useAuthStore } from "api/swr";
import { displayNameFromFields } from "commons/userDisplayName";
import { GitBranch, Loader2, Plus, RefreshCw } from "lucide-react";
import {
  SpineTemplate,
  isChildSpineTemplate,
} from "../../../types/spine-template";
import { getTemplateStateInfo } from "../../../types/templateState";
import { TemplateItem } from "../related-templates/TemplateItem";

export function ChildTemplatesPanel() {
  const { tid } = useParams<{ tid: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  const {
    data: template,
    isLoading: templateLoading,
  } = useSWR<SpineTemplate>(tid ? [`/spine-templates/${tid}`] : null);

  const {
    data: children,
    isLoading: childrenLoading,
    mutate: mutateChildren,
  } = useSWR<SpineTemplate[]>(
    tid ? [`/spine-templates/${tid}/children`] : null,
  );

  const { trigger: createChild, isMutating: isCreating } = useSWRMutation(
    tid ? `/spine-templates/${tid}/create-child` : null,
    postFetcher,
  );

  const { trigger: syncZip, isMutating: isSyncing } = useSWRMutation(
    tid ? `/spine-templates/${tid}/sync-zip-to-children` : null,
    postFetcher,
  );

  if (templateLoading || childrenLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader>加载子模版...</Loader>
      </div>
    );
  }

  if (!template || !tid) {
    return (
      <div className="text-center text-muted-foreground py-8">模板不存在</div>
    );
  }

  if (isChildSpineTemplate(template)) {
    return (
      <div className="text-center text-muted-foreground py-8 space-y-2">
        <p>当前为子模版，不能再创建或管理子模版。</p>
        {template.parentId && (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/admin/spine/editor/${template.parentId}`}>
              查看父模版
            </Link>
          </Button>
        )}
      </div>
    );
  }

  const handleCreateChild = async () => {
    try {
      const user = useAuthStore.getState().auth?.user;
      const result = await createChild({
        ...(user?.id ? { painter: user.id } : {}),
        ...(user
          ? { publisher: displayNameFromFields(user) || undefined }
          : {}),
      });
      await mutateChildren();
      toast({
        title: "子模版已创建",
        description: `ID: ${result.id}`,
      });
      navigate(`/admin/spine/editor/${result.id}`);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "创建失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    }
  };

  const handleSyncZip = async () => {
    try {
      const result = await syncZip({});
      await mutateChildren();
      setSyncDialogOpen(false);
      toast({
        title: "同步完成",
        description: `已将 Zip 同步到 ${result.syncedCount || 0} 个子模版（含 draft；线上 frame 经 worker 写 Redis）`,
        className: "bg-green-500 text-white",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "同步失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    }
  };

  const childList = children || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            子模版
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              {childList.length}
            </Badge>
          </h4>
          <p className="text-xs text-muted-foreground">
            从当前模版沿用 Zip 与 Cover/Preview，快速新建子模版（不含
            Selection / SKU）
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSyncDialogOpen(true)}
            disabled={isSyncing || childList.length === 0 || !template.spineZipUrl}
            title={
              !template.spineZipUrl
                ? "父模版尚无 Zip"
                : childList.length === 0
                  ? "暂无子模版"
                  : "同步 Zip 到所有子模版"
            }
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            同步 Zip 到所有子模版
          </Button>
          <Button
            size="sm"
            onClick={handleCreateChild}
            disabled={isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            创建子模版
          </Button>
        </div>
      </div>

      {childList.length === 0 ? (
        <div className="text-center text-muted-foreground py-10 border border-dashed rounded-lg">
          暂无子模版，点击上方按钮创建
        </div>
      ) : (
        <div className="space-y-2">
          {childList.map((child) => {
            const stateInfo = getTemplateStateInfo(child.state);
            return (
              <div
                key={child.id}
                className="flex flex-col sm:flex-row sm:items-stretch gap-2"
              >
                <div className="flex-1 min-w-0">
                  <TemplateItem
                    template={child}
                    showAction={false}
                    isSelected={false}
                  />
                </div>
                <div className="flex sm:flex-col items-center justify-end gap-2 shrink-0 sm:py-3 sm:pr-1">
                  {stateInfo && (
                    <div className="flex items-center gap-1 text-xs">
                      <Dot className={stateInfo.className} />
                      <span>{stateInfo.label}</span>
                    </div>
                  )}
                  <Button size="sm" variant="secondary" asChild>
                    <Link to={`/admin/spine/editor/${child.id}`}>编辑</Link>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>同步 Zip 到所有子模版？</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                将把当前父模版的 Zip、压缩 Zip、版本号与指纹，同步到全部{" "}
                {childList.length} 个子模版。
              </span>
              <span className="block font-medium text-destructive">
                是否全部替换，包括线上（template / draft / frame + Redis）？
              </span>
              <span className="block text-muted-foreground">
                不点此按钮则不会替换。已上线子模版会通过 worker
                同步 Zip，确保 Redis 与 Firestore 一致。
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSyncDialogOpen(false)}
              disabled={isSyncing}
            >
              取消
            </Button>
            <Button onClick={handleSyncZip} disabled={isSyncing}>
              {isSyncing ? "同步中..." : "确认全部替换（含线上）"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
